import { Pagination as MuiPagination } from '@mui/material'
import { Box, InputBase, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import DefaultItem from './PaginationItem'
import { useEffect, useState } from 'react'
import { Icon } from 'packages/eid-icons'
import { Select } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'

const StyledInput = styled(InputBase)(({ theme }) => ({
    width: 'auto',
    height: '4rem',
    margin: '0 0.8rem',
    borderRadius: '0.4rem',
    backgroundColor: '#fff',
    textAlignLast: 'center',
    border: 'solid 0.1rem #ebebed',
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
        display: 'none',
    },
    '& input[type=number]': {
        MozAppearance: 'textfield',
    },
    '& .MuiInputBase-input': {
        position: 'relative',
        fontSize: '1.6rem',
        color: '#aab0b4',
        padding: '0.95rem 0.6rem',
    },
    '&.Mui-focused': {
        boxShadow: '0 0.2rem 0.8rem 0 rgba(48, 127, 193, 0.15)',
        border: 'solid 0.1rem #0057A0',
    },
    '&.Mui-error': {
        border: 'solid 0.1rem red',
    },
}))

const PaginationList = styled('ul')(({ styles }) => ({
    ...styles,
}))

const GoToPageContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    marginLeft: '3rem',
    width: '2.4rem',
    height: '4rem',
    fontSize: '0.8rem',
    color: '#8b909a',
    textTransform: 'uppercase',
    wordBreak: 'break-word',
})

const GoToPageButton = styled(Box)({
    width: '4rem',
    height: '4rem',
    padding: '1.2rem',
    borderRadius: '0.4rem',
    border: 'solid 0.1rem #01ae8f',
    backgroundColor: '#01ae8f',
    '&:hover': {
        cursor: 'pointer',
    },
})

const PagePerItemContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    marginRight: '0.25rem',
    marginLeft: "1rem",
    height: '4rem',
    fontSize: '0.8rem',
    color: '#8b909a',
    textTransform: 'uppercase',
    wordBreak: 'break-word',
})

const PagePerItemSelect = styled(Box)({
    width: '100%',
})

const ItemCountContainer = styled(Box)({
    marginLeft: '3rem',
    '& p': {
        fontSize: '1.2rem !important',
        color: '#5d6870 !important',
        marginTop: '0.8rem',
        letterSpacing: '0.2rem',
    },
    '& h5': {
        fontSize: '0.8rem',
        textTransform: 'uppercase',
        color: '#8b909a',
    },
})

const Pagination = (props) => {
    const {
        size,
        listStyles,
        roundedStyles,
        PaginationItemComponent: PaginationItem,
        variant,
        minimumRowsPerPage,
        parentIndex,
        showPageSelection = false,
        onPageSelection,
        page,
        count,
        itemsPerPage,
        showItemsPerPageSelection = false,
        onItemsPerPageChange,
        totalCount,
        showCountStat = false,
        ...rest
    } = props

    const [goToPage, setGoToPage] = useState(page)

    useEffect(() => {
        setGoToPage(page)
    }, [page])

    const invalidGoToPage = !goToPage || goToPage < 1 || goToPage > count

    let itemsPerPageOptions = [
        {
            id: 10,
            value: 10,
        },
        {
            id: 15,
            value: 15,
        },
        {
            id: 25,
            value: 25,
        },
        {
            id: 50,
            value: 50,
        },
        {
            id: 75,
            value: 75,
        },
        {
            id: 100,
            value: 100,
        },
    ]

    const addItem = (data, item) => {
        if (data.findIndex((x) => x.value === item) < 0) {
            data.unshift({
                id: item,
                name: item.toString(),
                value: item,
            })
            data = data.sort((a, b) => a.value - b.value)
        }
        return data
    }

    if (showItemsPerPageSelection && itemsPerPage) {
        itemsPerPageOptions = addItem(itemsPerPageOptions, itemsPerPage)
        if (
            minimumRowsPerPage &&
            minimumRowsPerPage < 10 &&
            minimumRowsPerPage !== itemsPerPage
        ) {
            itemsPerPageOptions = addItem(
                itemsPerPageOptions,
                minimumRowsPerPage,
            )
        }
    }

    const minimumValuesPerPage = itemsPerPageOptions?.reduce(
        (min, item) => (item.value < min ? item.value : min),
        itemsPerPageOptions[0].value,
    )

    const getOptionLabel = (option) => option.id.toString()

    const { t } = useTranslation()

    const defaultListStyles = {
        '& .MuiPagination-ul': {
            borderRadius: '0.5rem',
            backgroundColor: '#ffffff',
            boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
            '& >li:first-of-type': {
                '& > button': {
                    borderRadius: '0.5rem 0 0 0.5rem !important',
                },
            },
            '& >li:last-of-type': {
                '& > button': {
                    borderRadius: '0 0.5rem 0.5rem 0 !important',
                },
            },
        },
        ...listStyles,
    }

    const defaultRoundedStyles = {
        '& .MuiPagination-ul': {
            backgroundColor: '#ffffff',
            borderRadius: '1.2rem',
            height: '2.4rem',
            fontSize: '1.2rem !important',
            boxShadow: '0 0.2rem 0.3rem 0 rgba(0, 0, 0, 0.08)',
            justifyContent: 'space-evenly',
            '& >li': {
                '& > button': {
                    borderRadius: '50%',
                    height: '1.5rem',
                    minWidth: '1.5rem',
                    margin: '0 0.5rem !important',
                    fontSize: '1.2rem !important',
                    border: 'none !important',
                },
                '& > div': {
                    borderRadius: '50%',
                    height: '1.5rem',
                    minWidth: '1.5rem',
                    border: 'none !important',
                    margin: '0 0.5rem !important',
                },
            },
        },
        ...roundedStyles,
    }

    const styles =
        variant === 'rounded' ? defaultRoundedStyles : defaultListStyles

    const getStartOfPage = () => {
        if (page === 1) {
            return 1
        }

        return (page - 1) * itemsPerPage + 1
    }
    const getEndOfPage = () => {
        return page * itemsPerPage > totalCount
            ? totalCount
            : page * itemsPerPage
    }

    const handleGoToPageKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onPageSelection(Number(goToPage))
        }
    }

    const minimumPageSize = minimumRowsPerPage || minimumValuesPerPage

    if (totalCount <= minimumPageSize || totalCount === 0) {
        return null
    }

    return (
        <Box
            display="flex"
            data-ai-explain={`Pagination controls: navigate pages of results — currently on page ${page} of ${count}${showItemsPerPageSelection ? `, showing ${itemsPerPage} items per page` : ''}${showCountStat && totalCount ? `, ${totalCount} total items` : ''}`}
            data-ai-explain-section="listing"
        >
            <MuiPagination
                sx={{
                    zIndex: parentIndex ? parentIndex : 9999,
                    ...styles,
                }}
                component={PaginationList}
                styles={styles}
                renderItem={(item) => (
                    <PaginationItem size={size} page={page} {...item} />
                )}
                size={size}
                page={page}
                count={count}
                {...rest}
            />
            {showPageSelection && onPageSelection && (
                <Box display="flex">
                    <GoToPageContainer>{t('GoToPage')}</GoToPageContainer>
                    <StyledInput
                        value={goToPage}
                        onChange={(e) => setGoToPage(e.target.value)}
                        type="number"
                        inputProps={{ min: 1, max: count }}
                        error={invalidGoToPage}
                    />
                    {!invalidGoToPage && goToPage !== page && (
                        <GoToPageButton
                            role="button"
                            tabIndex={0}
                            aria-label="Go to page"
                            onClick={() => onPageSelection(Number(goToPage))}
                            onKeyDown={handleGoToPageKeyDown}
                        >
                            <Icon
                                name="ArrowLong"
                                color="#fff"
                                direction="right"
                            />
                        </GoToPageButton>
                    )}
                </Box>
            )}
            {itemsPerPage &&
                showItemsPerPageSelection &&
                onItemsPerPageChange && (
                    <Box display="flex">
                        <PagePerItemContainer>
                            {t('ItemsPerPage')}
                        </PagePerItemContainer>
                        <PagePerItemSelect>
                            <Select
                                value={{
                                    id: itemsPerPage,
                                    name: itemsPerPage.toString(),
                                    value: itemsPerPage,
                                }}
                                options={itemsPerPageOptions}
                                getOptionLabel={getOptionLabel}
                                onChange={(e) => onItemsPerPageChange(e.value)}
                            />
                        </PagePerItemSelect>
                    </Box>
                )}
            {showCountStat && totalCount && (
                <ItemCountContainer>
                    <Typography variant="body1">{`${getStartOfPage()}-${getEndOfPage()}/${totalCount}`}</Typography>
                    <Typography variant="h5">{t('Items')}</Typography>
                </ItemCountContainer>
            )}
        </Box>
    )
}

Pagination.defaultProps = {
    PaginationItemComponent: DefaultItem,
}

export default Pagination
